import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import clsx from 'clsx';

import { useEffect, useMemo, useState } from 'react';
import { IconArrowLeft } from '../../../Common/Icon';
import { useCategoryByIdContext } from './CategoryByIdProvider';
import { useCategory } from '../../../../store/category';
import CarouselScroll from '../../../Common/CarouselScroll';
import TipForm from './TipForm';
import CategoryChart from '../../Home/HowMuchToTip/CategoryStatistics/CategoryChart';
import Average from '../../Home/HowMuchToTip/CategoryStatistics/Average';
import TakeSurvey from './TakeSurvey';
import TakeSurveyWorkers from './TakeSurveyWorkers';
import { useSubCategory } from '../../../../store/subCategory';
import CategoryDetails from '../../Home/HowMuchToTip/CategoryDetails';
import Comments from '../../../Common/Comments';
import RadioGroupTipType from '../../../Common/RadioGroupTipType';
import { SubCategoryTipStatisticsData } from '../../../../hooks/api/subCategoryTip';
import QuestionStatistic from './QuestionStatistic';
import { useQuizStatuses } from './useQuizStatuses';

import { filterQuestionsByUserType } from './helpers';
import DisplayHtmlContent from '../../../Common/DisplayHtmlContent';

import styles from './index.module.scss';

function CategoryViewContent() {
  const { categoryId, subCategoryId } = useParams();
  const navigate = useNavigate();
  const [_, setSearchParams] = useSearchParams();
  const {
    category, subCategory, displayMode, setDisplayMode,
  } = useCategoryByIdContext();

  const { listAll } = useCategory();
  const { byId } = useSubCategory();
  const { response: quizStatus, getQuizStatus } = useQuizStatuses();
  // const { getTipStatistic, response, loading } = useTipStatistic();

  // eslint-disable-next-line max-len
  const [subCategoryTipStatisticsData, setSubCategoryTipStatisticsData] = useState<SubCategoryTipStatisticsData | null>(null);

  useEffect(() => {
    document.getElementById('scrollContainerMain')?.scrollTo(0, 0);
  }, [category, subCategory]);

  const workersQuestions = useMemo(
    () => filterQuestionsByUserType(subCategory?.questions || [], 'worker'),
    [subCategory],
  );

  const customersQuestions = useMemo(
    () => filterQuestionsByUserType(subCategory?.questions || [], 'customer'),
    [subCategory],
  );

  const getQuizStatuses = (subCategoryIdProp: string | undefined) => {
    if (subCategoryIdProp) {
      getQuizStatus(subCategoryIdProp);
    }
  };

  const afterCreateTip = () => {
    setSearchParams({ refetchTips: 'yes' }, { replace: true });
    setTimeout(() => setSearchParams({}, { replace: true }), 100);
  };

  useEffect(() => {
    getQuizStatuses(subCategoryId);
  }, [subCategoryId]);

  console.log('subCategory', subCategory);

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.content}>
          <Button
            className={styles.btnBack}
            type="text"
            onClick={() => navigate('/categories')}
          >
            <IconArrowLeft className={styles.btnBackIcon} />
            All Categories
          </Button>

          <Spin spinning={byId.loading}>
            <div className={styles.innerContainer}>
              {subCategory && (
                <h2 className={styles.title}>{subCategory.header}</h2>
              )}
              <h2
                className={clsx(
                  styles.title,
                  // subCategory ? styles.titleSubCagegory : styles.title,
                )}
              >
                {category?.name}
              </h2>

              <CarouselScroll
                className={styles.subcategoryCarousel}
                loading={listAll.loading}
                activeId={subCategory?.id}
                hideControls={(category?.subCategories?.length || 0) <= 6}
              >
                {category?.subCategories.map(({ id, header }) => (
                  <Button
                    id={id}
                    key={id}
                    className={clsx(styles.btnSubCategory, {
                      [styles.btnSubCategoryActive]: subCategoryId === id,
                    })}
                    type="text"
                    onClick={() => navigate(`/categories/${category.id}/${id}`, {
                      replace: true,
                    })}
                  >
                    {header}
                  </Button>
                ))}
              </CarouselScroll>

              {subCategory ? (
                <>
                  {subCategory?.description ? (
                    <DisplayHtmlContent
                      html={subCategory.description}
                      className={styles.description}
                    />
                  ) : null}

                  <div className={styles.keywords}>
                    <div className={styles.label}>Included Search Terms:</div>
                    <div>{subCategory.displayKeywords?.join(', ')}</div>
                  </div>

                  <div className={styles.grayContainer}>
                    <div className={styles.tipFormWrapper}>
                      <TipForm
                        subCategoryId={subCategory.id}
                        afterSubmit={afterCreateTip}
                      />
                    </div>

                    <div className={styles.chartWrapper}>
                      <div className={styles.average}>
                        <RadioGroupTipType
                          value={displayMode}
                          onChange={setDisplayMode}
                          light
                        />{' '}
                        <Average
                          light
                          subCategoryTipStatisticsData={
                            subCategoryTipStatisticsData
                          }
                          displayMode={displayMode}
                        />
                      </div>
                      <CategoryChart
                        light={false}
                        displayMode={displayMode}
                        subCategoryId={subCategory?.id}
                        setAverageData={setSubCategoryTipStatisticsData}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {category?.description ? (
                    <DisplayHtmlContent
                      html={category.description}
                      className={styles.description}
                    />
                  ) : null}

                  <CategoryDetails
                    category={category}
                    subCategoryIdActive={subCategory || null}
                    setSubCategory={(subCategoryProp) => navigate(
                      `/categories/${categoryId}/${subCategoryProp?.id}`,
                    )}
                    theme="light"
                    howCategoryLink={false}
                  />
                </>
              )}

              {customersQuestions.length > 0 ? (
                <div className={styles.surveyWrapper}>
                  {quizStatus?.data.isCustomerQuizDone ? (
                    <QuestionStatistic
                      questions={customersQuestions}
                      subCategoryId={subCategoryId || ''}
                      userType="customer"
                    />
                  ) : (
                    <TakeSurvey
                      questions={customersQuestions}
                      onSuccess={() => getQuizStatuses(subCategoryId)}
                    />
                  )}
                </div>
              ) : null}

              {workersQuestions.length > 0 ? (
                <div className={styles.surveyWrapper}>
                  {quizStatus?.data.isWorkerQuizDone ? (
                    <QuestionStatistic
                      subCategoryId={subCategoryId || ''}
                      questions={workersQuestions}
                      userType="worker"
                    />
                  ) : (
                    <TakeSurveyWorkers
                      questions={workersQuestions}
                      onSuccess={() => getQuizStatuses(subCategoryId)}
                    />
                  )}
                </div>
              ) : null}

              <div className={styles.commentsWrapper}>
                <Comments
                  filter={{
                    category: subCategoryId ? undefined : categoryId,
                    subCategory: subCategoryId,
                    entity: subCategoryId ? 'subcategory' : 'category',
                  }}
                />
              </div>
            </div>
          </Spin>
        </div>
      </div>
    </div>
  );
}

export default CategoryViewContent;
